import {
  useProjectShipments,
  useProjectFields,
  useProjectCarrierMappings,
  useProjectFileStats,
} from '@/core/api/queries';
import { useMemo } from 'react';

/**
 * Custom hook that aggregates all project-shipment related data.
 * Combines multiple queries needed for shipment table functionality.
 *
 * @param {string | null} projectId - The project ID to fetch data for
 * @param {boolean} pollShipments - Whether to enable polling for shipments
 * @returns {Object} Aggregated project-shipment data with combined loading states
 */
export const useProjectShipmentData = (
  projectId: string | null,
  pollShipments: boolean = false,
  searchParams?: URLSearchParams,
) => {
  // Fetch all project-shipment related queries
  const projectShipments = useProjectShipments(projectId || '', pollShipments, searchParams);
  const projectFields = useProjectFields(projectId || '', undefined);

  const projectCarrierMappings = useProjectCarrierMappings(projectId || '');

  const projectFileStats = useProjectFileStats(projectId || '');

  // Extract data for easy access
  const shipments = projectShipments.data || [];
  const fields = projectFields.data || [];

  const enrichedFields = useMemo(
    () => [
      ...fields,
      {
        key: 'clientReference',
        label: 'Client Reference',
        dataType: 'text' as const,
        groups: [],
      },
    ],
    [fields],
  );

  const carrierMappings = projectCarrierMappings.data || [];
  const fileStats = projectFileStats.data || null;

  // Create tracking jobs map for quick lookup
  const trackingJobsMap: Record<string, any> = {};

  // Combined loading and error states
  const isLoading =
    projectShipments.isLoading ||
    projectFields.isLoading ||
    projectCarrierMappings.isLoading ||
    projectFileStats.isLoading;

  const isError =
    projectShipments.isError || projectFields.isError || projectCarrierMappings.isError || projectFileStats.isError;

  const isFetching =
    projectShipments.isFetching ||
    projectFields.isFetching ||
    projectCarrierMappings.isFetching ||
    projectFileStats.isFetching;

  return {
    // Raw query objects (for refetch, etc.)
    queries: {
      projectShipments,
      projectFields,
      projectCarrierMappings,
      projectFileStats,
    },

    // Extracted data
    shipments,
    fields: enrichedFields,
    carrierMappings,
    fileStats,

    trackingJobsMap,

    // Combined states
    isLoading,
    isError,
    isFetching,

    // Granular loading states (useful for skeleton UIs)
    loadingStates: {
      shipments: projectShipments.isLoading,
      fields: projectFields.isLoading,
      carrierMappings: projectCarrierMappings.isLoading,
      fileStats: projectFileStats.isLoading,
    },

    // Error details
    errors: {
      shipments: projectShipments.error,
      fields: projectFields.error,
      carrierMappings: projectCarrierMappings.error,
      fileStats: projectFileStats.error,
    },

    // Refetch all queries
    refetchAll: () => {
      projectShipments.refetch();
      projectFields.refetch();
      projectCarrierMappings.refetch();
      projectFileStats.refetch();
    },
  };
};
